"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import {
	ArrowRight,
	Brain,
	CircleCheck,
	Droplets,
	Dumbbell,
	ExternalLink,
	FlaskConical,
	HeartPulse,
	LucideIcon,
	Scale,
	ShieldCheck,
	Sparkles,
	Target,
	Waves,
	BookOpen,
} from "lucide-react";
import posthog from "posthog-js";
import { Button } from "@/ui/components/ui/button";
import quizQuestions from "@/data/quiz-questions.json";
import type { QuizResponse } from "@/lib/quiz-scoring";
import { QuizLivePool } from "./quiz-live-pool";
import { QuizResultsView } from "./quiz-results-view";

interface QuizFlowClientProps {
	locale: string;
	channel: string;
}

const STEP_SLUGS = ["goal", "priority", "dosing_frequency"] as const;
const STEP_LABEL_KEYS: Record<(typeof STEP_SLUGS)[number], string> = {
	goal: "progress.goal",
	priority: "progress.priority",
	dosing_frequency: "progress.dose",
};

const OPTION_ICONS: Record<string, Record<string, LucideIcon>> = {
	goal: {
		recovery: HeartPulse,
		"anti-aging": Sparkles,
		performance: Dumbbell,
		weight: Scale,
		skin: Droplets,
		cognitive: Brain,
	},
	priority: {
		evidence: BookOpen,
		safety: ShieldCheck,
		efficacy: Target,
		cost: Waves,
	},
	dosing_frequency: {
		once_weekly: CircleCheck,
		"2_3x_weekly": CircleCheck,
		daily: CircleCheck,
		no_preference: CircleCheck,
	},
};

type Question = (typeof quizQuestions)[number];

function ProgressBar({ step, t }: { step: number; t: ReturnType<typeof useTranslations> }) {
	return (
		<ol className="flex items-center gap-2" aria-label="Quiz progress">
			{STEP_SLUGS.map((slug, i) => {
				const state = i < step ? "done" : i === step ? "current" : "future";
				return (
					<li key={slug} className="flex flex-1 items-center gap-2">
						<span
							className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold ${
								state === "done"
									? "bg-primary text-primary-foreground"
									: state === "current"
										? "border-2 border-primary bg-background text-primary"
										: "border border-border bg-background text-muted-foreground"
							}`}
							aria-current={state === "current" ? "step" : undefined}
						>
							{state === "done" ? "✓" : i + 1}
						</span>
						<span
							className={`text-xs font-medium ${
								state === "future" ? "text-muted-foreground" : "text-foreground"
							}`}
						>
							{t(STEP_LABEL_KEYS[slug])}
						</span>
						{i < STEP_SLUGS.length - 1 && (
							<span
								className={`ml-1 h-px flex-1 rounded ${i < step ? "bg-primary" : "bg-border"}`}
								aria-hidden
							/>
						)}
					</li>
				);
			})}
		</ol>
	);
}

export function QuizFlowClient({ locale, channel }: QuizFlowClientProps) {
	const t = useTranslations("quiz");
	const router = useRouter();
	const [step, setStep] = useState(0);
	const [answers, setAnswers] = useState<Record<string, string>>({});
	const [results, setResults] = useState<QuizResponse | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [showMobilePool, setShowMobilePool] = useState(false);

	const startedRef = useRef(false);
	useEffect(() => {
		if (!startedRef.current) {
			startedRef.current = true;
			posthog.capture("quiz_started");
		}
	}, []);

	const questions = quizQuestions as Question[];
	const currentQuestion = questions[step];
	const done = results != null;

	const submitFinal = useCallback(
		async (finalAnswers: Record<string, string>) => {
			setLoading(true);
			setError(false);
			try {
				const r = await fetch("/api/quiz/recommend", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ answers: finalAnswers, channel, locale }),
				});
				if (!r.ok) throw new Error(`HTTP ${r.status}`);
				const data = (await r.json()) as QuizResponse;
				setResults(data);
				posthog.capture("quiz_completed", {
					recommended_slugs: data.recommendations.map((x) => x.peptide.slug),
					recommendation_count: data.recommendations.length,
				});
			} catch {
				setError(true);
			} finally {
				setLoading(false);
			}
		},
		[channel, locale],
	);

	const handleAnswer = useCallback(
		(value: string) => {
			if (!currentQuestion) return;
			const next = { ...answers, [currentQuestion.slug]: value };
			setAnswers(next);
			posthog.capture("quiz_step_completed", {
				step_number: step,
				question_slug: currentQuestion.slug,
				answer: value,
			});
			if (step >= questions.length - 1) {
				void submitFinal(next);
			} else {
				setStep(step + 1);
			}
		},
		[answers, currentQuestion, questions.length, step, submitFinal],
	);

	const onEditAnswer = useCallback((stepIndex: number) => {
		setResults(null);
		setStep(stepIndex);
	}, []);

	const onRestart = useCallback(() => {
		setAnswers({});
		setResults(null);
		setError(false);
		setStep(0);
	}, []);

	const onPoolPick = useCallback(
		(slug: string) => {
			posthog.capture("quiz_live_pool_item_clicked", { peptide_slug: slug });
			router.push(`/${locale}/${channel}/products/${slug}`);
		},
		[channel, locale, router],
	);

	// Parse shareable answers from URL on mount: `?goal=...&priority=...&dose=...`.
	// When all 3 are valid, prefill + auto-submit so shared links land on the result view.
	const hydratedRef = useRef(false);
	useEffect(() => {
		if (hydratedRef.current) return;
		hydratedRef.current = true;
		if (typeof window === "undefined") return;
		const sp = new URLSearchParams(window.location.search);
		const goal = sp.get("goal");
		const priority = sp.get("priority");
		const dose = sp.get("dose");
		const optSet = (slug: keyof typeof OPTION_ICONS, v: string | null) => v && v in OPTION_ICONS[slug];
		if (optSet("goal", goal) && optSet("priority", priority) && optSet("dosing_frequency", dose)) {
			const shared: Record<string, string> = {
				goal: goal!,
				priority: priority!,
				dosing_frequency: dose!,
			};
			// eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot URL->state hydration on mount (hydratedRef-guarded); lazy-initialising from window.location would desync SSR/CSR markup (React #418), and the auto-submit fetch must live in an effect anyway
			setAnswers(shared);
			void submitFinal(shared);
		}
	}, [submitFinal]);

	const sortedOptions = useMemo(
		() => (currentQuestion ? [...currentQuestion.options].sort((a, b) => a.order - b.order) : []),
		[currentQuestion],
	);

	const poolAnswerCount = Object.keys(answers).length;

	// Auto-open the mobile live pool the first time the visitor answers, so matches surface
	// without an extra tap. Only fires once — the user can still collapse it afterwards.
	const poolAutoOpenedRef = useRef(false);
	useEffect(() => {
		if (!poolAutoOpenedRef.current && poolAnswerCount > 0) {
			poolAutoOpenedRef.current = true;
			setShowMobilePool(true);
		}
	}, [poolAnswerCount]);

	return (
		<div className="mx-auto w-full max-w-5xl px-4 pt-6 sm:px-6 sm:pt-8">
			{/* Eyebrow + title + subtitle — inside the frame, not a page header */}
			<div className="mb-6 text-center">
				<p className="bg-muted/50 inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-primary">
					<FlaskConical className="h-3 w-3" aria-hidden />
					{t("eyebrow")}
				</p>
				<h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">{t("title")}</h1>
				<p className="mt-2 text-sm text-muted-foreground sm:text-base">{t("subtitle")}</p>
			</div>

			<section className="overflow-hidden rounded-2xl border border-border bg-card shadow-lg">
				{/* Top bar: progress + escape hatch */}
				<div className="bg-muted/30 flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4 sm:px-7">
					<div className="min-w-0 flex-1">
						<ProgressBar step={done ? STEP_SLUGS.length : step} t={t} />
					</div>
					<Link
						href={`/${locale}/${channel}/peptides`}
						className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
					>
						{t("catalogEscapeHatch")}
						<ExternalLink className="h-3 w-3" aria-hidden />
					</Link>
				</div>

				{/* Body */}
				{done && results ? (
					<div className="p-5 sm:p-8">
						<QuizResultsView
							results={results}
							answers={answers}
							locale={locale}
							channel={channel}
							onEditAnswer={onEditAnswer}
							onRestart={onRestart}
						/>
					</div>
				) : (
					<div className="grid md:grid-cols-[1.15fr_0.85fr]">
						{/* Left: question */}
						<div className="p-5 sm:p-8 md:border-r md:border-border">
							{error ? (
								<div className="border-destructive/30 bg-destructive/5 rounded-xl border p-6 text-center">
									<h2 className="text-lg font-semibold">{t("results.error.title")}</h2>
									<p className="mt-2 text-sm text-muted-foreground">{t("results.error.subtitle")}</p>
									<Button className="mt-4" onClick={onRestart}>
										{t("retake")}
									</Button>
								</div>
							) : loading ? (
								<div
									className="flex h-full min-h-[360px] flex-col items-center justify-center gap-3"
									aria-live="polite"
									aria-busy
								>
									<div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
									<p className="text-sm text-muted-foreground">{t("results.loading")}</p>
								</div>
							) : currentQuestion ? (
								<>
									<p className="text-[11px] font-semibold uppercase tracking-widest text-primary">
										{t("progress.stepOf", { current: step + 1, total: questions.length })}
									</p>
									<h2 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">
										{t(`questions.${currentQuestion.slug}.label`)}
									</h2>
									<div className="mt-5 grid gap-2.5">
										{sortedOptions.map((opt) => {
											const selected = answers[currentQuestion.slug] === opt.value;
											const Icon = OPTION_ICONS[currentQuestion.slug]?.[opt.value];
											const label = t(`questions.${currentQuestion.slug}.options.${opt.value}.label`);
											const sub = t(`questions.${currentQuestion.slug}.options.${opt.value}.sub`);
											return (
												<button
													key={opt.value}
													type="button"
													onClick={() => handleAnswer(opt.value)}
													className={`group flex items-center gap-4 rounded-xl border px-4 py-3 text-left transition ${
														selected
															? "border-primary bg-primary text-primary-foreground shadow-md"
															: "hover:bg-muted/30 border-border bg-card hover:border-primary"
													}`}
													aria-pressed={selected}
												>
													{Icon && (
														<span
															className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg ${
																selected
																	? "bg-primary-foreground/15 text-primary-foreground"
																	: "bg-muted text-primary"
															}`}
														>
															<Icon className="h-5 w-5" aria-hidden />
														</span>
													)}
													<span className="min-w-0 flex-1">
														<span className="block text-sm font-semibold">{label}</span>
														<span
															className={`mt-0.5 block text-xs ${
																selected ? "text-primary-foreground/75" : "text-muted-foreground"
															}`}
														>
															{sub}
														</span>
													</span>
													<ArrowRight
														className={`h-4 w-4 shrink-0 ${
															selected
																? "text-primary-foreground/60"
																: "text-muted-foreground/50 group-hover:text-primary"
														}`}
														aria-hidden
													/>
												</button>
											);
										})}
									</div>

									<div className="mt-5 flex items-center justify-between text-xs text-muted-foreground">
										{step > 0 ? (
											<button type="button" onClick={() => setStep(step - 1)} className="hover:text-primary">
												← {t("back")}
											</button>
										) : (
											<span />
										)}
										<span className="inline-flex items-center gap-1.5">
											<ShieldCheck className="h-3.5 w-3.5" aria-hidden />
											{t("noEmailRequired")}
										</span>
									</div>
								</>
							) : null}
						</div>

						{/* Right: desktop live pool. Mobile: collapsible sheet. */}
						<aside className="hidden md:block">
							<QuizLivePool answers={answers} channel={channel} locale={locale} onPick={onPoolPick} />
						</aside>

						<div className="border-t border-border md:hidden">
							<button
								type="button"
								onClick={() => setShowMobilePool((s) => !s)}
								className="flex w-full items-center justify-between px-5 py-3 text-sm"
								aria-expanded={showMobilePool}
							>
								<span className="font-semibold">
									{poolAnswerCount === 0
										? t("livePool.zeroState")
										: t("livePool.mobileToggle", { count: poolAnswerCount })}
								</span>
								<span className="text-muted-foreground">
									{showMobilePool ? t("livePool.mobileHide") : "↓"}
								</span>
							</button>
							{showMobilePool && (
								<div className="p-5">
									<QuizLivePool answers={answers} channel={channel} locale={locale} onPick={onPoolPick} />
								</div>
							)}
						</div>
					</div>
				)}
			</section>

			{/* Inline trust micro-copy below card */}
			<div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
				<span className="inline-flex items-center gap-1.5">
					<ShieldCheck className="h-3.5 w-3.5" aria-hidden />
					{t("trustInline.research")}
				</span>
				<span aria-hidden>·</span>
				<span className="inline-flex items-center gap-1.5">
					<FlaskConical className="h-3.5 w-3.5" aria-hidden />
					{t("trustInline.coa")}
				</span>
				<span aria-hidden>·</span>
				<span className="inline-flex items-center gap-1.5">
					<CircleCheck className="h-3.5 w-3.5" aria-hidden />
					{t("trustInline.batch")}
				</span>
			</div>
		</div>
	);
}
